import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Eye, Trash2, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { useAuth } from "../AuthContext";
import { StatusChip, Btn, ConfirmDialog, Modal, inputCls, Thumb } from "../components";

const ProjectsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/projects").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await adminApi.post("/projects", { title: newTitle });
      toast.success("Draft project created");
      navigate(`/admin/projects/${r.data.id}`);
    } catch (err) {
      toast.error(fmtErr(err));
      setBusy(false);
    }
  };

  const togglePublish = async (p) => {
    try {
      await adminApi.post(`/projects/${p.id}/${p.status === "published" ? "unpublish" : "publish"}`);
      toast.success(p.status === "published" ? "Project unpublished" : "Project published — now live on the website");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await adminApi.delete(`/projects/${del.id}`);
      toast.success("Project deleted");
      setDel(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const filtered = (items || []).filter((p) =>
    `${p.data?.title} ${p.data?.client} ${p.data?.location}`.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="admin-projects-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Projects</h1>
          <p className="mt-1 text-sm text-slate-500">Published projects appear automatically on the public Projects page.</p>
        </div>
        <Btn onClick={() => setNewOpen(true)} data-testid="add-project-btn"><Plus size={14} /> Add New Project</Btn>
      </div>

      <div className="mt-6 relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className={`${inputCls} pl-9`} placeholder="Search projects…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="projects-search-input" />
      </div>

      <div className="mt-5 bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              {["", "Project", "Client", "Status", "Updated", "Actions"].map((h) => (
                <th key={h} className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items === null ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No projects found.</td></tr>
            ) : (
              filtered.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`project-row-${p.id}`}>
                  <td className="px-5 py-3"><Thumb src={p.data?.img} alt="" /></td>
                  <td className="px-5 py-3">
                    <p className="font-semibold text-slate-900 line-clamp-1">{p.data?.title}</p>
                    {p.status === "published" && p.data?.featured && <span className="text-[10px] font-bold uppercase tracking-widest text-[#C5221F]">Featured</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-600">{p.data?.client || "—"}</td>
                  <td className="px-5 py-3"><StatusChip status={p.status} /></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(p.updated_at)}<br /><span className="text-slate-400">{p.updated_by}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5">
                      <Link to={`/admin/projects/${p.id}`} className="p-2 text-slate-500 hover:text-[#C5221F]" title="Edit" data-testid={`edit-project-${p.id}`}><Pencil size={15} /></Link>
                      {p.status === "published" && (
                        <a href={`/projects/${p.id}`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-[#C5221F]" title="View live"><Eye size={15} /></a>
                      )}
                      <button onClick={() => togglePublish(p)} className="p-2 text-slate-500 hover:text-[#C5221F]" title={p.status === "published" ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${p.id}`}>
                        <RotateCcw size={15} className={p.status === "published" ? "" : "rotate-180"} />
                      </button>
                      {user.role === "super_admin" && (
                        <button onClick={() => setDel(p)} className="p-2 text-slate-400 hover:text-red-600" title="Delete" data-testid={`delete-project-${p.id}`}><Trash2 size={15} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal open={newOpen} onClose={() => setNewOpen(false)} title="Add New Project">
        <form onSubmit={create} className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Project Title</label>
            <input className={inputCls} value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required minLength={2}
              placeholder="e.g. Reactor Package — XYZ Refinery" data-testid="new-project-title-input" />
          </div>
          <p className="text-xs text-slate-500">A draft will be created — it only goes live when you publish it.</p>
          <div className="flex justify-end gap-3">
            <Btn variant="ghost" type="button" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="create-project-submit-btn">Create Draft</Btn>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!del}
        title="Delete this project?"
        message={`"${del?.data?.title}" will be permanently removed from the website and the CMS. This cannot be undone.`}
        onConfirm={doDelete}
        onClose={() => setDel(null)}
        busy={busy}
      />
    </div>
  );
};

export default ProjectsList;
